import { Fragment, useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import lumalLogo from '../assets/lumal-logo.svg';

export interface NavItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Grupo del sidebar; se inserta un encabezado cuando cambia respecto al item previo. */
  group?: string;
}

export interface AppShellProps {
  title: string;
  subtitle?: string;
  /** Marca opcional sobre la inicial (p.ej. logo del tenant). */
  logoUrl?: string;
  /** Slot a la derecha del topbar (user menu, etc.). */
  actions?: ReactNode;
  /** Título de la sección activa, mostrado en el topbar (estilo dashboard). */
  heading?: ReactNode;
  /** Si se provee, renderiza un layout con sidebar de navegación (estilo dashboard). */
  nav?: { items: NavItem[]; active: string; onNavigate: (key: string) => void };
  /** Clave de persistencia del estado colapsado del sidebar (localStorage). */
  storageKey?: string;
  children: ReactNode;
}

// Hook de estado colapsado persistido en localStorage. El default es expandido;
// en SSR/entornos sin window cae a expandido sin romper.
function useCollapsed(storageKey: string): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return globalThis.localStorage?.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(storageKey, collapsed ? '1' : '0');
    } catch {
      /* no-op: modo privado / storage bloqueado */
    }
  }, [storageKey, collapsed]);
  return [collapsed, toggle];
}

export function AppShell({
  title,
  subtitle,
  logoUrl,
  actions,
  heading,
  nav,
  storageKey = 'lumal:sidebar-collapsed',
  children,
}: AppShellProps) {
  const [collapsed, toggleCollapsed] = useCollapsed(storageKey);
  // Grupos del sidebar colapsables (hide/unhide), persistido en localStorage.
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('lumal:nav-groups-collapsed') || '[]'); } catch { return []; }
  });
  const toggleGroup = (g: string) => setCollapsedGroups((prev) => {
    const next = prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g];
    try { localStorage.setItem('lumal:nav-groups-collapsed', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Sin logo de tenant, cae al logo OFICIAL de Lumal Care (no a la inicial).
  const isTenantLogo = Boolean(logoUrl);
  const resolvedLogo = logoUrl ?? lumalLogo;
  const brand = (
    <div style={brandWrap}>
      <img src={resolvedLogo} alt="" style={logoImg} />
      {!collapsed && (
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle} data-testid="app-title">{title}</div>
          {subtitle ? <div style={subtitleStyle}>{subtitle}</div> : null}
          {/* Wordmark sutil cuando el logo es el de un tenant (white-label). */}
          {isTenantLogo ? <div style={wordmark}>Lumal Care</div> : null}
        </div>
      )}
    </div>
  );

  // ----- Layout con sidebar (dashboard) -----
  if (nav) {
    const activeItem = nav.items.find((it) => it.key === nav.active);
    return (
      <div data-testid="app-shell" style={sidebarShell} data-collapsed={collapsed}>
        <aside style={{ ...sidebar, width: collapsed ? 64 : 244 }} data-testid="app-sidebar">
          <div style={accentStrip} />
          <div style={{ padding: collapsed ? '0.9rem 0 0.4rem' : '0.9rem 0.9rem 0.4rem', display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start' }}>
            {brand}
          </div>
          <nav style={navList} data-testid="app-nav">
            {nav.items.map((it, idx) => {
              const active = it.key === nav.active;
              const showHeader = !collapsed && !!it.group && it.group !== nav.items[idx - 1]?.group;
              const hidden = !!it.group && collapsedGroups.includes(it.group);
              return (
                <Fragment key={it.key}>
                  {showHeader && (
                    <button type="button" style={navGroupLabel} onClick={() => toggleGroup(it.group!)}
                      data-testid={`nav-group-${it.key}`} aria-expanded={!hidden} title={it.group}>
                      <span>{it.group}</span>
                      <span style={{ fontSize: '0.7rem' }}>{hidden ? '▸' : '▾'}</span>
                    </button>
                  )}
                  {(collapsed || !hidden) && (
                    <button onClick={() => nav.onNavigate(it.key)}
                      data-testid={`nav-${it.key}`} data-active={active}
                      title={collapsed ? it.label : undefined}
                      style={{
                        ...navItem,
                        ...(active ? navItemActive : {}),
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '0.55rem 0' : '0.55rem 0.7rem',
                      }}>
                      <span style={navIcon}>{it.icon}</span>
                      {!collapsed && <span style={navLabel}>{it.label}</span>}
                    </button>
                  )}
                </Fragment>
              );
            })}
          </nav>
          <div style={sidebarFooter}>
            <ThemeToggle />
            {/* Sin "· demo": esto corre en instalaciones de cliente en producción. */}
            {!collapsed && <small>Lumal Care</small>}
          </div>
        </aside>
        <div style={mainCol}>
          <header style={topbar}>
            <div style={topbarLeft}>
              <button
                type="button"
                onClick={toggleCollapsed}
                data-testid="sidebar-toggle"
                aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                aria-expanded={!collapsed}
                title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                style={iconBtn}
              >
                <HamburgerIcon />
              </button>
              {heading ? <div style={topbarHeading} data-testid="topbar-heading">{heading}</div>
                : activeItem ? <div style={topbarHeading} data-testid="topbar-heading">{activeItem.label}</div>
                : null}
            </div>
            <div style={topbarActions}>
              {actions}
            </div>
          </header>
          <main style={mainScroll}>
            <div style={mainContentInner}>{children}</div>
          </main>
        </div>
      </div>
    );
  }

  // ----- Layout simple (topbar) -----
  return (
    <div data-testid="app-shell" style={shellStyle}>
      <div style={accentStrip} />
      <header style={headerStyle}>
        {brand}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <ThemeToggle />
          {actions}
        </div>
      </header>
      <main style={mainStyle}>{children}</main>
      <footer style={footerStyle}><small>Lumal Care</small></footer>
    </div>
  );
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden focusable="false">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

// ThemeToggle: alterna claro/oscuro. Persiste en localStorage y aplica data-theme en <html>.
// Sin preferencia guardada se sigue la del SISTEMA (prefers-color-scheme) en vez de forzar
// claro. Quien aplica el tema ANTES del primer paint es el script en línea de index.html
// (si React lo hiciera al montar, el usuario de tema oscuro vería un flash blanco en cada
// carga); aquí sólo se LEE lo que ese script ya dejó puesto. Disponible en todas las apps
// por estar en AppShell.
function ThemeToggle() {
  const [theme, setTheme] = useState<string>(() => {
    try {
      const applied = globalThis.document?.documentElement.getAttribute('data-theme');
      if (applied) return applied;
    } catch { /* sin DOM */ }
    return resolveInitialTheme();
  });
  // Red por si la app no lleva el script en línea: deja el atributo puesto igualmente.
  useEffect(() => {
    try {
      const root = globalThis.document?.documentElement;
      if (root && !root.getAttribute('data-theme')) root.setAttribute('data-theme', theme);
    } catch { /* sin DOM */ }
  }, [theme]);
  const toggle = useCallback(() => {
    setTheme((cur) => {
      const next = cur === 'light' ? 'dark' : 'light';
      try { globalThis.document?.documentElement.setAttribute('data-theme', next); } catch { /* sin DOM */ }
      try { globalThis.localStorage?.setItem('lumal:theme', next); } catch { /* storage bloqueado */ }
      return next;
    });
  }, []);
  return (
    <button type="button" onClick={toggle} data-testid="theme-toggle"
      aria-label={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
      title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'} style={iconBtn}>
      {theme === 'light' ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}

// Misma resolución que el script en línea de index.html: preferencia guardada, si no la del
// sistema. Sirve de red por si una app aún no lleva ese script.
function resolveInitialTheme(): string {
  try {
    const saved = globalThis.localStorage?.getItem('lumal:theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* storage bloqueado */ }
  try {
    if (globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch { /* sin matchMedia */ }
  return 'light';
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden focusable="false">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

const accentStrip: CSSProperties = { height: 4, background: 'var(--lumal-gradient, var(--lumal-primary))' };
const brandWrap: CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 };
const logoImg: CSSProperties = { width: 36, height: 36, borderRadius: 8, objectFit: 'contain', flexShrink: 0 };
const titleStyle: CSSProperties = { fontSize: '1.05rem', fontWeight: 680, color: 'var(--lumal-fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const subtitleStyle: CSSProperties = { color: 'var(--lumal-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const wordmark: CSSProperties = {
  fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
  color: 'var(--lumal-muted)', marginTop: 1, opacity: 0.75,
};

// sidebar layout
// El shell ocupa exactamente el viewport y NO scrollea como un todo: el sidebar y el
// topbar quedan fijos y SOLO <main> scrollea (overflow:auto). Así los thead sticky se
// anclan a top:0 del área scrolleable (debajo del topbar) sin depender de su altura.
const sidebarShell: CSSProperties = { height: '100vh', overflow: 'hidden', display: 'flex', background: 'var(--lumal-bg)', color: 'var(--lumal-fg)' };
const sidebar: CSSProperties = {
  flexShrink: 0, display: 'flex', flexDirection: 'column',
  background: 'var(--lumal-surface)', borderRight: '1px solid var(--lumal-border)', position: 'sticky', top: 0, height: '100vh',
  transition: 'width 0.16s ease', overflow: 'hidden',
};
const navList: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, padding: '0.5rem', overflowY: 'auto', overflowX: 'hidden', flex: 1 };
const navGroupLabel: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
  color: 'var(--lumal-muted)', padding: '0.8rem 0.7rem 0.3rem',
};
const navItem: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8,
  border: 'none', background: 'transparent', color: 'var(--lumal-muted)', cursor: 'pointer',
  fontSize: '0.92rem', textAlign: 'left', width: '100%', fontWeight: 500,
};
const navItemActive: CSSProperties = {
  background: 'color-mix(in srgb, var(--lumal-primary-text) 20%, transparent)', color: 'var(--lumal-fg)', fontWeight: 650,
  boxShadow: 'inset 3px 0 0 var(--lumal-primary-text)',
};
const navIcon: CSSProperties = { display: 'grid', placeItems: 'center', width: 18, height: 18, color: 'var(--lumal-accent-text)', flexShrink: 0 };
const navLabel: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const sidebarFooter: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '0.6rem 0.9rem', borderTop: '1px solid var(--lumal-border)', color: 'var(--lumal-muted)' };
const mainCol: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 };
const topbar: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
  padding: '0.6rem 1.5rem', background: 'var(--lumal-surface)', borderBottom: '1px solid var(--lumal-border)',
  zIndex: 10, minHeight: 56, flexShrink: 0, boxShadow: 'var(--lumal-shadow-sm)',
};
const topbarLeft: CSSProperties = { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 };
const topbarHeading: CSSProperties = { fontSize: '1.15rem', fontWeight: 680, color: 'var(--lumal-fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const topbarActions: CSSProperties = { display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 };
const iconBtn: CSSProperties = {
  display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: 8,
  border: '1px solid var(--lumal-border)', background: 'var(--lumal-surface-2, transparent)', color: 'var(--lumal-fg)',
  cursor: 'pointer', flexShrink: 0,
};
// <main> es el contenedor de scroll y NO lleva padding: así los thead sticky se anclan
// al borde superior real (sin el hueco que dejaba el padding-top del scroller). El relleno
// vive en el wrapper interno, que sí scrollea.
const mainScroll: CSSProperties = { flex: 1, minHeight: 0, overflow: 'auto' };
const mainContentInner: CSSProperties = { padding: '1.5rem', width: '100%' };

// topbar simple layout
const shellStyle: CSSProperties = { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--lumal-bg)', color: 'var(--lumal-fg)' };
const headerStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.9rem', padding: '0.85rem 1.5rem',
  background: 'var(--lumal-surface)', borderBottom: '1px solid var(--lumal-border)', boxShadow: 'var(--lumal-shadow-sm)',
};
const mainStyle: CSSProperties = { flex: 1, padding: '1.5rem', maxWidth: 1120, width: '100%', margin: '0 auto' };
const footerStyle: CSSProperties = {
  borderTop: '1px solid var(--lumal-border)', padding: '0.75rem 1.5rem', textAlign: 'center',
  color: 'var(--lumal-muted)', background: 'var(--lumal-surface)',
};
